import { useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import { InputField } from "../../../components/forms/InputField";
import { QuestionWithRelations } from "../../../../../api/src/types";
import { validateQuestionnaireNumber } from "../utils/questionnaire";
import { useGetCurrentUserQuery } from "../../../api/users";
import {
  useCreateAnswerMutation,
  useUpdateAnswerMutation,
} from "../../../api/answers";
import { IconContainer, ErrorWrapper, InputWrapper } from "./SharedFormStyles";
import useIsMobile from "../../../hooks/useIsMobile";
import GreenTick from "../../../components/icons/GreenTick";
import {
  IconSize,
  QUESTIONNAIRE_INPUT_PLACEHOLDER_TEXT,
} from "../../../components/theme";
import Loader from "../../../components/icons/Loader";

interface NumberFieldInputProps {
  question: QuestionWithRelations;
}

const NumberFieldInput: React.FC<NumberFieldInputProps> = ({ question }) => {
  const isMobile = useIsMobile();
  const [numberInput, setNumberInput] = useState<number | null>(
    question.answers[0]?.valueTyped
      ? Number(question.answers[0].valueTyped)
      : null,
  );
  const [numberError, setNumberError] = useState<string | null>(null);

  const { data: user } = useGetCurrentUserQuery();
  const [
    createAnswer,
    {
      isLoading: isCreating,
      isError: isCreateError,
      isSuccess: isCreateSuccess,
      reset: resetCreateAnswer,
    },
  ] = useCreateAnswerMutation();
  const [
    updateAnswer,
    {
      isLoading: isUpdating,
      isError: isUpdateError,
      isSuccess: isUpdateSuccess,
      reset: resetUpdateAnswer,
    },
  ] = useUpdateAnswerMutation();

  const hasAnswer = question.answers.length > 0;

  const save = useDebouncedCallback((value: string | null) => {
    if (!user) return;

    if (!hasAnswer && !value) return;

    const payload = {
      questionId: question.id,
      userId: user.id,
      valueTyped: value,
    };

    if (!hasAnswer) void createAnswer(payload);
    else void updateAnswer(payload);
  }, 1000);

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    resetCreateAnswer();
    resetUpdateAnswer();

    const value = e.target.value;
    // Only set numeric value if it's not empty and is a valid number
    setNumberInput(value && !isNaN(Number(value)) ? Number(value) : null);

    const error = validateQuestionnaireNumber(value, question);
    setNumberError(error);
    if (error) {
      save(null);
      return;
    }
    save(value);
  };

  const isLoading = isCreating || isUpdating;
  const hasError = isCreateError || isUpdateError;
  const isSuccess =
    (isCreateSuccess || isUpdateSuccess) &&
    !hasError &&
    !isLoading &&
    !numberError &&
    (numberInput != null || !question.isRequired);

  return (
    <>
      <InputWrapper>
        <InputField
          type="number"
          placeholder={QUESTIONNAIRE_INPUT_PLACEHOLDER_TEXT}
          value={numberInput ? String(numberInput) : ""}
          onChange={(e) => {
            handleNumberChange(e);
          }}
          required={question.isRequired}
          errorMessage={numberError}
        />
        {isLoading && (
          <IconContainer>
            <Loader spinnerOnly={true} size={isMobile ? 16 : 34} />
          </IconContainer>
        )}
        {isSuccess && (
          <IconContainer>
            <GreenTick size={isMobile ? IconSize.S : IconSize.XL} />
          </IconContainer>
        )}
      </InputWrapper>

      {hasError && (
        <ErrorWrapper>
          Error saving answer, please refresh the page and try again
        </ErrorWrapper>
      )}
    </>
  );
};

export default NumberFieldInput;
